import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { HelpCircle, Search, ChevronDown, ChevronUp, BookOpen, GraduationCap, Shield, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DOMPurify from 'dompurify';

const CATEGORY_CONFIG = {
  etudiant: { label: 'Étudiant', icon: GraduationCap, color: '#2E6CF5', bg: '#eff6ff' },
  instructeur: { label: 'Instructeur', icon: Shield, color: '#F4621C', bg: '#fff7ed' },
  general: { label: 'Général', icon: BookOpen, color: '#002768', bg: '#f0f4ff' },
};

// Strict sanitizer: only allow inline strong/em/b/i tags (markdown output of MarkdownText)
const sanitize = (html) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: ['strong', 'em', 'b', 'i'],
  ALLOWED_ATTR: [],
});

const MarkdownText = ({ text }) => {
  // Simple markdown: **bold**, \n for line breaks, - for lists
  const lines = text.split('\n');
  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        const isList = line.trim().startsWith('- ') || line.trim().startsWith('* ');
        const content = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        const numbered = line.match(/^(\d+)\.\s/);

        if (isList) {
          return (
            <div key={i} className="flex items-start gap-2 ml-4">
              <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#002768' }} />
              <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: sanitize(content.replace(/^[-*]\s/, '')) }} />
            </div>
          );
        }
        if (numbered) {
          return (
            <div key={i} className="flex items-start gap-2 ml-4">
              <span className="text-sm font-bold flex-shrink-0" style={{ color: '#002768' }}>{numbered[1]}.</span>
              <span className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: sanitize(content.replace(/^\d+\.\s/, '')) }} />
            </div>
          );
        }
        return <p key={i} className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitize(content) }} />;
      })}
    </div>
  );
};

const Help = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openArticles, setOpenArticles] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const resp = await axios.get(`${API}/help/articles`);
      setArticles(resp.data);
      // Open first article by default
      if (resp.data.length > 0) {
        setOpenArticles(new Set([resp.data[0].id]));
      }
    } catch (err) {
      console.error('Error loading help articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleArticle = (id) => {
    setOpenArticles(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = articles.filter(a => {
    const matchesSearch = !search ||
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.content.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = activeFilter === 'all' || a.category === activeFilter;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: '#2E6CF5', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="help-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <HelpCircle className="w-8 h-8" style={{ color: '#002768' }} />
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: '#002768' }}>
            Centre d'aide
          </h1>
        </div>
        <p className="text-gray-600">
          Tout ce que vous devez savoir pour utiliser le Simulateur 911
        </p>
      </div>

      {/* Search + Filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher dans l'aide..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-10 py-2.5 border-2 rounded-lg focus:ring-2 focus:outline-none text-sm"
            style={{ borderColor: '#e5e7eb', '--tw-ring-color': '#2E6CF5' }}
            data-testid="help-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: 'all', label: 'Tous', color: '#6b7280' },
            { key: 'etudiant', label: 'Étudiant', color: '#2E6CF5' },
            { key: 'instructeur', label: 'Instructeur', color: '#F4621C' },
            { key: 'general', label: 'Général', color: '#002768' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className="px-3 py-1 rounded-full text-xs font-medium transition-all border-2"
              style={{
                borderColor: f.color,
                backgroundColor: activeFilter === f.key ? f.color : 'transparent',
                color: activeFilter === f.key ? 'white' : f.color,
              }}
              data-testid={`help-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-xs text-gray-400 ml-2">{filtered.length} article{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Articles Accordion */}
      <div className="space-y-2">
        {filtered.map(article => {
          const isOpen = openArticles.has(article.id);
          const catConfig = CATEGORY_CONFIG[article.category] || CATEGORY_CONFIG.general;
          const CatIcon = catConfig.icon;

          return (
            <Card key={article.id} className="border-2 overflow-hidden transition-all" style={{ borderColor: isOpen ? catConfig.color : '#e5e7eb' }}>
              <button
                onClick={() => toggleArticle(article.id)}
                className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                data-testid={`help-article-toggle-${article.id}`}
              >
                <CatIcon className="w-5 h-5 flex-shrink-0" style={{ color: catConfig.color }} />
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-sm" style={{ color: '#002768' }}>{article.title}</span>
                </div>
                <Badge variant="outline" className="text-xs flex-shrink-0 text-white" style={{ backgroundColor: catConfig.color }}>
                  {catConfig.label}
                </Badge>
                {isOpen ? (
                  <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {isOpen && (
                <CardContent className="pt-0 pb-5 px-5 border-t" style={{ backgroundColor: catConfig.bg }}>
                  <div className="pt-4">
                    <MarkdownText text={article.content} />
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun article ne correspond à votre recherche</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Help;
